import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';
import { useAnalytics } from '../context/AnalyticsContext';

const { FiArrowRight, FiDollarSign, FiCheck, FiPercent, FiClock, FiFileText, FiShield, FiPhone, FiCalendar } = FiIcons;

const Financing = () => {
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicle');
  const { getVehicleById } = useInventory();
  const { trackEvent } = useAnalytics();
  const vehicle = vehicleId ? getVehicleById(parseInt(vehicleId)) : null;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleInterest: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : '',
    creditScore: 'excellent',
    downPayment: '',
    tradeIn: 'no',
    tradeInValue: '',
    employmentStatus: '',
    income: '',
    comments: ''
  });
  const [showFinanceForm, setShowFinanceForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [calculatorValues, setCalculatorValues] = useState({
    vehiclePrice: vehicle ? vehicle.price : 50000,
    downPayment: 5000,
    tradeInValue: 0,
    term: 60,
    rate: 3.99
  });
  const [calculatorResult, setCalculatorResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;
    setCalculatorValues({
      ...calculatorValues,
      [name]: parseFloat(value)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('form_fill', { type: 'financing_application' });
    setFormSubmitted(true);
    
    // In a real implementation, you'd send the form data to a server
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleInterest: '',
        creditScore: 'excellent',
        downPayment: '',
        tradeIn: 'no',
        tradeInValue: '',
        employmentStatus: '',
        income: '',
        comments: ''
      });
      setFormSubmitted(false);
      setShowFinanceForm(false);
    }, 3000);
  };

  const calculatePayment = (e) => {
    e.preventDefault();
    
    const { vehiclePrice, downPayment, tradeInValue, term, rate } = calculatorValues;
    const loanAmount = vehiclePrice - downPayment - tradeInValue;
    const monthlyRate = rate / 100 / 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
    
    setCalculatorResult({
      monthlyPayment: monthlyPayment.toFixed(2),
      totalInterest: ((monthlyPayment * term) - loanAmount).toFixed(2),
      totalCost: (monthlyPayment * term).toFixed(2)
    });
    
    trackEvent('calculator_use', { type: 'finance_calculator', vehiclePrice });
  };

  const financingOptions = [
    {
      name: 'Standard Financing',
      description: 'Traditional financing with competitive rates and flexible terms.',
      features: [
        'Rates as low as 2.9% APR',
        'Terms from 24 to 84 months',
        'No prepayment penalties',
        'Quick approval process'
      ]
    },
    {
      name: 'Audi Lease',
      description: 'Drive a new Audi with lower monthly payments and flexible end-of-term options.',
      features: [
        'Lower monthly payments',
        'Flexible lease terms (24-48 months)',
        'Multiple mileage options',
        'End-of-lease purchase option'
      ]
    },
    {
      name: 'Certified Pre-Owned Financing',
      description: 'Special rates and terms for Audi Certified Pre-Owned vehicles.',
      features: [
        'Competitive rates from 3.9% APR',
        'Extended warranty coverage',
        '300+ point inspection',
        '24/7 roadside assistance'
      ]
    }
  ];

  const specialOffers = [
    {
      title: '0% APR Financing',
      description: 'For qualified buyers on select new Audi models.',
      terms: 'For up to 60 months with approved credit. Expires soon.'
    },
    {
      title: '1.9% APR for 72 Months',
      description: 'On all new 2024 Audi Q5 models.',
      terms: 'For qualified buyers with approved credit through Audi Financial Services.'
    },
    {
      title: '$1,500 Conquest Bonus',
      description: 'When you trade in a qualifying non-Audi luxury vehicle.',
      terms: 'Must finance through Audi Financial Services. See dealer for details.'
    },
    {
      title: 'First-Time Buyer Program',
      description: 'Special rates for qualified first-time buyers with limited credit history.',
      terms: 'Income verification required. Restrictions apply.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=1920&h=600&fit=crop&crop=center" 
            alt="Audi financing" 
            className="w-full h-full object-cover opacity-60" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-tight">
              Audi Financing & Leasing
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light">
              Flexible financial solutions tailored to your needs, with competitive rates and personalized options.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setShowFinanceForm(true);
                  trackEvent('button_click', { type: 'financing_application' });
                }}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Apply for Financing
              </button>
              <a
                href="#calculator"
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Payment Calculator
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Selected Vehicle (if applicable) */}
      {vehicle && (
        <section className="py-8 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-full md:w-1/4">
                <img 
                  src={vehicle.images[0]} 
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} 
                  className="w-full h-40 object-cover rounded-lg shadow-md" 
                />
              </div>
              <div className="w-full md:w-3/4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Financing Options for Your Selected Vehicle
                </h2>
                <div className="flex flex-wrap gap-4 mb-4">
                  <div>
                    <span className="text-sm text-gray-500">Vehicle:</span>
                    <p className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Price:</span>
                    <p className="font-medium text-red-600">${vehicle.price.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Est. Monthly Payment:</span>
                    <p className="font-medium">${Math.round(vehicle.price / 60).toLocaleString()}/mo*</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  *Estimated payment with $5,000 down at 3.99% APR for 60 months. Does not include taxes, fees, or incentives. 
                  Use our calculator below for a personalized estimate.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Financing Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Financing Options
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from a variety of financing and leasing options designed to fit your budget and lifestyle.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {financingOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                    {option.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {option.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      setShowFinanceForm(true);
                      setFormData(prev => ({...prev, comments: `Interested in ${option.name}`}));
                      trackEvent('button_click', { type: 'financing_option', option: option.name });
                    }}
                    className="w-full bg-black text-white py-3 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
                  >
                    Learn More
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Calculator */}
      <section id="calculator" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Payment Calculator
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Estimate your monthly payments based on vehicle price, down payment, and financing terms.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200 shadow-sm"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Calculate Your Payment
              </h3>
              <form onSubmit={calculatePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="vehiclePrice"
                      value={calculatorValues.vehiclePrice}
                      onChange={handleCalculatorChange}
                      min="0"
                      step="1000"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Down Payment
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="downPayment"
                      value={calculatorValues.downPayment}
                      onChange={handleCalculatorChange}
                      min="0"
                      step="500"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trade-In Value (if applicable)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="tradeInValue"
                      value={calculatorValues.tradeInValue}
                      onChange={handleCalculatorChange}
                      min="0"
                      step="500"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loan Term (months)
                    </label>
                    <select
                      name="term"
                      value={calculatorValues.term}
                      onChange={handleCalculatorChange}
                      className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="24">24 months</option>
                      <option value="36">36 months</option>
                      <option value="48">48 months</option>
                      <option value="60">60 months</option>
                      <option value="72">72 months</option>
                      <option value="84">84 months</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interest Rate (APR %)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="rate"
                        value={calculatorValues.rate}
                        onChange={handleCalculatorChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <SafeIcon icon={FiPercent} className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white py-3 px-6 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
                    <span>Calculate Payment</span>
                  </button>
                </div>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200 shadow-sm"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Payment Summary
              </h3>
              {calculatorResult ? (
                <div>
                  <div className="bg-gray-50 p-6 mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Vehicle Price:</span>
                      <span className="font-semibold">${calculatorValues.vehiclePrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Down Payment:</span>
                      <span className="font-semibold">- ${calculatorValues.downPayment.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Trade-In Value:</span>
                      <span className="font-semibold">- ${calculatorValues.tradeInValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-gray-700">Amount Financed:</span>
                      <span className="font-semibold">
                        ${(calculatorValues.vehiclePrice - calculatorValues.downPayment - calculatorValues.tradeInValue).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Term:</span>
                      <span className="font-semibold">{calculatorValues.term} months</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Interest Rate:</span>
                      <span className="font-semibold">{calculatorValues.rate}% APR</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Total Interest:</span>
                      <span className="font-semibold">${calculatorResult.totalInterest}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Total Cost:</span>
                      <span className="font-semibold">${calculatorResult.totalCost}</span>
                    </div>
                  </div>

                  <div className="bg-red-600 text-white p-6 text-center">
                    <div className="text-sm uppercase tracking-wider mb-1">Estimated Monthly Payment</div>
                    <div className="text-4xl font-bold">${calculatorResult.monthlyPayment}</div>
                  </div>

                  <div className="mt-6 text-sm text-gray-500">
                    <p>This calculation is an estimate only. Actual payment may vary based on final price, taxes, fees, and incentives. Contact our finance department for a personalized quote.</p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowFinanceForm(true);
                        trackEvent('button_click', { type: 'financing_after_calculator' });
                      }}
                      className="w-full bg-black text-white py-3 px-6 font-medium hover:bg-gray-900 transition-colors duration-200 uppercase tracking-wider text-sm"
                    >
                      Apply for Financing
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                  <SafeIcon icon={FiDollarSign} className="w-16 h-16 text-gray-300 mb-4" />
                  <h4 className="text-xl font-light text-gray-700 mb-2">
                    Your Payment Details
                  </h4>
                  <p className="text-gray-500 max-w-xs mx-auto">
                    Use the calculator to see your estimated monthly payment and total loan cost.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Special Financing Offers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Current Special Offers
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Take advantage of these limited-time financing and leasing promotions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {specialOffers.map((offer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 border border-gray-200 p-6"
              >
                <div className="flex items-start space-x-3">
                  <div className="bg-red-600 p-3">
                    <SafeIcon icon={FiPercent} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                      {offer.title}
                    </h3>
                    <p className="text-gray-600 mb-3">{offer.description}</p>
                    <p className="text-sm text-gray-500 italic">{offer.terms}</p>
                    <button
                      onClick={() => {
                        setShowFinanceForm(true);
                        setFormData(prev => ({...prev, comments: `Interested in ${offer.title}`}));
                        trackEvent('button_click', { type: 'special_offer', offer: offer.title });
                      }}
                      className="mt-4 text-red-600 hover:text-red-700 transition-colors inline-flex items-center space-x-1 uppercase tracking-wider text-sm"
                    >
                      <span>Get Details</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financing Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Our Financing Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We make financing your new Audi simple, transparent, and stress-free.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                step: 1,
                title: 'Apply Online',
                description: 'Complete our secure online application in just minutes.',
                icon: FiFileText
              },
              {
                step: 2,
                title: 'Quick Review',
                description: 'Our finance team reviews your application and explores options.',
                icon: FiClock
              },
              {
                step: 3,
                title: 'Personalized Options',
                description: 'We present tailored financing solutions that fit your budget.',
                icon: FiDollarSign
              },
              {
                step: 4,
                title: 'Drive Home',
                description: 'Sign your paperwork and drive away in your new Audi.',
                icon: FiCheck
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 text-center border border-gray-200 shadow-sm"
              >
                <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold text-lg">{step.step}</span>
                </div>
                <div className="bg-gray-100 p-3 mb-4 inline-block rounded-full">
                  <SafeIcon icon={step.icon} className="w-6 h-6 text-gray-700" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => {
                setShowFinanceForm(true);
                trackEvent('button_click', { type: 'financing_process_cta' });
              }}
              className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
            >
              <span>Start Your Application</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about financing and leasing your Audi.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                question: 'What credit score do I need to finance an Audi?',
                answer: 'While higher credit scores typically qualify for better rates, we work with a range of credit profiles. Our finance team partners with multiple lenders to find solutions for various credit situations. Even if you have less-than-perfect credit, we may have options for you.'
              },
              {
                question: 'Should I lease or finance my Audi?',
                answer: 'This depends on your preferences and driving habits. Leasing typically offers lower monthly payments and the ability to drive a new vehicle every few years. Financing builds equity and has no mileage restrictions. Our finance team can help you compare both options based on your specific situation.'
              },
              {
                question: 'How much should my down payment be?',
                answer: 'Down payment amounts vary based on your credit profile and the vehicle. While some qualified buyers may be eligible for zero down payment offers, a down payment of 10-20% of the vehicle price typically results in better terms and lower monthly payments.'
              },
              {
                question: 'Can I get pre-approved before I shop?',
                answer: 'Yes! We recommend getting pre-approved before shopping. This helps you understand your budget and streamlines the purchase process once you find your perfect Audi. Pre-approval applications can be completed online, by phone, or in person at our dealership.'
              },
              {
                question: 'What documents do I need to apply for financing?',
                answer: 'Typically, you\'ll need a valid driver\'s license, proof of income (pay stubs or tax returns), proof of residence (utility bill or lease agreement), and insurance information. For specific situations, additional documentation may be required.'
              },
              {
                question: 'Can I refinance my Audi loan later?',
                answer: 'Yes, refinancing is an option if your credit improves or interest rates drop. We can help you explore refinancing options to potentially lower your payment or reduce your loan term. Contact our finance department to discuss if refinancing makes sense for your situation.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 border border-gray-200"
              >
                <h3 className="text-xl font-medium text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Finance Team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Finance Department Information
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Hours</h4>
                  <div className="space-y-1 text-gray-600">
                    <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                    <p>Saturday: 9:00 AM - 5:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Contact</h4>
                  <div className="space-y-1">
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-900">Phone:</span> (902) 455-7890 ext. 4
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-900">Email:</span> finance@halifaxaudi.ca
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Address</h4>
                  <p className="text-gray-600">
                    6077 Columbus St<br />
                    Halifax, NS B3K 0E9
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Our Finance Team
              </h3>
              <p className="text-gray-600 mb-6">
                Our experienced finance team is dedicated to finding the best financing or leasing solution for your needs. We work with multiple lenders to secure competitive rates and flexible terms.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Personalized Service</h4>
                    <p className="text-gray-600">
                      We take the time to understand your needs and preferences.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Multiple Lender Relationships</h4>
                    <p className="text-gray-600">
                      We shop multiple lenders to find you the best rate and terms.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Transparent Process</h4>
                    <p className="text-gray-600">
                      We clearly explain all terms and options so you can make informed decisions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Privacy Protection</h4>
                    <p className="text-gray-600">
                      Your personal and financial information is always kept secure and confidential.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+19024557890"
                    className="flex-1 bg-black text-white py-3 px-4 font-medium hover:bg-gray-900 transition-colors text-center flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
                  >
                    <SafeIcon icon={FiPhone} className="w-4 h-4" />
                    <span>Call Finance Team</span>
                  </a>
                  <button
                    onClick={() => {
                      setShowFinanceForm(true);
                      trackEvent('button_click', { type: 'contact_finance_team' });
                    }}
                    className="flex-1 bg-red-600 text-white py-3 px-4 font-medium hover:bg-red-700 transition-colors text-center flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
                  >
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>Schedule Consultation</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Ready to Finance Your New Audi?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-light">
              Take the first step toward driving your dream Audi. Apply for financing today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setShowFinanceForm(true);
                  trackEvent('button_click', { type: 'financing_cta' });
                }}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Apply for Financing
              </button>
              <Link 
                to="/inventory"
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Browse Inventory
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Finance Application Modal */}
      {showFinanceForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            {formSubmitted ? (
              <div className="text-center py-6">
                <div className="bg-green-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Application Submitted
                </h3>
                <p className="text-gray-600 mb-4">
                  Thank you for your financing application. A member of our finance team will contact you shortly to discuss your options.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Financing Application
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number*
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle of Interest
                    </label>
                    <input
                      type="text"
                      name="vehicleInterest"
                      value={formData.vehicleInterest}
                      onChange={handleInputChange}
                      placeholder="e.g., 2024 Audi Q5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credit Score Range
                    </label>
                    <select
                      name="creditScore"
                      value={formData.creditScore}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="excellent">Excellent (720+)</option>
                      <option value="good">Good (680-719)</option>
                      <option value="fair">Fair (620-679)</option>
                      <option value="poor">Poor (below 620)</option>
                      <option value="unknown">I don't know</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Down Payment Amount
                    </label>
                    <input
                      type="text"
                      name="downPayment"
                      value={formData.downPayment}
                      onChange={handleInputChange}
                      placeholder="e.g., $5,000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Do you have a trade-in?
                    </label>
                    <select
                      name="tradeIn"
                      value={formData.tradeIn}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {formData.tradeIn === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estimated Trade-In Value
                      </label>
                      <input
                        type="text"
                        name="tradeInValue"
                        value={formData.tradeInValue}
                        onChange={handleInputChange}
                        placeholder="e.g., $10,000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employment Status
                    </label>
                    <select
                      name="employmentStatus"
                      value={formData.employmentStatus}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">Select an option</option>
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="self-employed">Self-employed</option>
                      <option value="retired">Retired</option>
                      <option value="student">Student</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Income
                    </label>
                    <input
                      type="text"
                      name="income"
                      value={formData.income}
                      onChange={handleInputChange}
                      placeholder="e.g., $75,000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Comments
                    </label>
                    <textarea
                      name="comments"
                      value={formData.comments}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Please provide any additional information that might help us with your application"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    ></textarea>
                  </div>
                  <div className="text-xs text-gray-500">
                    <p>By submitting this form, you authorize Halifax Audi to obtain your credit report and verify other information provided. This will not affect your credit score.</p>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                      Submit Application
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowFinanceForm(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Financing;